import { ThemeModeType } from 'enums/ThemeModeType';
import React from 'react';
import { services } from 'services/container';
import messageListStyles from 'src/components/Chat/ChatContent/MessageList/MessageList.less';

import { requestBrowserPermission } from './native';
import o9n from './orientation';
import screenfull from './screenfull';

export const loadStyleSheet = async (href: string) => {
    const styleSheet = document.createElement('link');
    styleSheet.rel = 'stylesheet';
    styleSheet.type = 'text/css';
    styleSheet.href = href;
    styleSheet.crossOrigin = '';

    return new Promise<HTMLStyleElement>((res, rej) => {
        let loaded = false;

        styleSheet.addEventListener(
            'load',
            () => {
                loaded = true;
                res(styleSheet);
            },
            { once: true },
        );

        setTimeout(() => {
            if (!loaded) {
                rej();
                document.head.removeChild(styleSheet);
            }
        }, 15_000);

        document.head.appendChild(styleSheet);
    });
};

export const getClickPos = (e: React.MouseEvent) => {
    if (!e.currentTarget) {
        return { x: 0, y: 0 };
    }
    let [x, y] = [e.clientX, e.clientY];

    if (!x || !y) {
        const rect = e.currentTarget.getBoundingClientRect();
        [x, y] = [rect.left, rect.top];
    }

    return { x, y };
};

export const getNodeRect = (e?: HTMLDivElement | Element): DOMRect =>
    e?.getBoundingClientRect() ??
    ({
        bottom: 0,
        height: 0,
        left: 0,
        right: 0,
        top: 0,
        width: 0,
        x: 0,
        y: 0,
    } as DOMRect);

export const determineElementInViewPort = (el: HTMLElement, fullyVisible = true) => {
    const { top, left, bottom, right } = el.getBoundingClientRect();
    const { innerHeight, innerWidth } = window;

    if (fullyVisible) {
        return top >= 0 && left >= 0 && bottom <= innerHeight && right <= innerWidth;
    }

    return (
        ((top > 0 && top < innerHeight) || (bottom > 0 && bottom < innerHeight)) &&
        ((left > 0 && left < innerWidth) || (right > 0 && right < innerWidth))
    );
};

type SwipeEvent<T = Element> = React.TouchEvent<T> | TouchEvent;
export type TCurrentSwipeDirection = 'left' | 'right' | 'down' | 'up' | null;
export const createSwipeHandlers = ({
    handlers = {},
    minSwipeDist = 5,
    live = false,
}: {
    handlers?: {
        left?: (dist: number, e: SwipeEvent) => void;
        right?: (dist: number, e: SwipeEvent) => void;
        up?: (dist: number, e: SwipeEvent) => void;
        down?: (dist: number, e: SwipeEvent) => void;
        end?: (
            relativeDistSwiped: number,
            currentSwipeDirection: TCurrentSwipeDirection,
            e: SwipeEvent,
        ) => void;
    };
    minSwipeDist?: number;
    live?: boolean;
}) => {
    let currentSwipeDirection: TCurrentSwipeDirection = null;
    let xDown: number | null = null;
    let yDown: number | null = null;
    let relativeDistSwiped: number | null = null;

    const getSwipeDistances = (e: SwipeEvent) => {
        const xUp = e.touches[0].clientX;
        const yUp = e.touches[0].clientY;

        const xDiff = (xDown as number) - xUp;
        const xDiffAbs = Math.abs(xDiff);
        const yDiff = (yDown as number) - yUp;
        const yDiffAbs = Math.abs(yDiff);

        return {
            xDiff,
            xDiffAbs,
            yDiff,
            yDiffAbs,
        };
    };

    return {
        swipeStart: (e: SwipeEvent) => {
            xDown = e.touches[0].clientX;
            yDown = e.touches[0].clientY;
        },
        swipeMove: (e: SwipeEvent) => {
            if (!xDown || !yDown) {
                return;
            }

            const { xDiff, xDiffAbs, yDiff, yDiffAbs } = getSwipeDistances(e);

            if (xDiffAbs > yDiffAbs && xDiffAbs > minSwipeDist) {
                relativeDistSwiped = xDiffAbs;
                if (xDiff > 0) {
                    currentSwipeDirection = 'left';
                    handlers?.left?.(xDiffAbs, e);
                } else {
                    currentSwipeDirection = 'right';
                    handlers?.right?.(xDiffAbs, e);
                }
            } else if (yDiffAbs > minSwipeDist) {
                relativeDistSwiped = yDiffAbs;
                if (yDiff > 0) {
                    currentSwipeDirection = 'up';
                    handlers?.up?.(yDiffAbs, e);
                } else {
                    currentSwipeDirection = 'down';
                    handlers?.down?.(yDiffAbs, e);
                }
            }

            if (!live) {
                xDown = null;
                yDown = null;
                relativeDistSwiped = null;
                currentSwipeDirection = null;
            }
        },
        swipeEnd: (e: SwipeEvent) => {
            handlers.end?.(relativeDistSwiped as number, currentSwipeDirection, e);

            if (live) {
                xDown = null;
                yDown = null;
                relativeDistSwiped = null;
                currentSwipeDirection = null;
            }
        },
    };
};

export const noop = () => {};

export const saveScrollPos = (list?: HTMLElement) => {
    let curChildIdx = 0;
    let firstVisibleChild: HTMLElement;
    let storedPos = 0;

    if (!list) return noop;

    const messages = list.querySelectorAll('[data-test-id="webchat_message"]');

    while ((firstVisibleChild = messages[curChildIdx++] as HTMLElement)) {
        storedPos =
            firstVisibleChild.getBoundingClientRect().bottom - list.getBoundingClientRect().top;

        if (storedPos > 0) break;
    }

    const anchorUuid = firstVisibleChild?.dataset.uuid;

    if (!anchorUuid) return noop;

    return () => {
        const anchor = list.querySelector(`[data-uuid="${anchorUuid}"`);

        if (!anchor) return;

        const curAnchorPos =
            anchor.getBoundingClientRect().bottom - list.getBoundingClientRect().top;

        list.scrollTop += curAnchorPos - storedPos;
    };
};

const curScript = document.currentScript as HTMLScriptElement;

export const staticUrl = (url: string) => {
    const scriptUrl = curScript.src;

    const path = scriptUrl.substring(0, scriptUrl.lastIndexOf('/'));

    return path + '/static/' + url;
};

export const elementHasValidChildren = (el: React.ReactElement) =>
    React.Children.toArray(el.props.children).filter((c) => React.isValidElement(c)).length > 0;

// #region triggerAnimation
type TriggerAnimationParams = {
    node: HTMLElement;
    animationName: string;
    className?: string;
    removableClassName?: boolean;
    forceEndMs?: number;
    onAnimationEnd?: () => void;
};

export const triggerAnimation = ({
    node,
    animationName,
    className,
    removableClassName = true,
    forceEndMs = 300,
    onAnimationEnd,
}: TriggerAnimationParams) => {
    let animationStarted = false;

    if (node) {
        if (className && !node.classList.contains(className)) {
            node.classList.add(className);
        }

        node.addEventListener(
            'animationstart',
            (e) => {
                if (e.animationName === animationName) {
                    animationStarted = true;
                }
            },
            { once: true },
        );

        node.addEventListener(
            'animationend',
            (e) => {
                if (e.animationName === animationName) {
                    if (className && removableClassName) node.classList.remove(className);
                    if (onAnimationEnd) onAnimationEnd();
                }
            },
            { once: true },
        );
    }

    setTimeout(() => {
        if (!animationStarted) {
            if (onAnimationEnd) onAnimationEnd();
        }
    }, forceEndMs);
};
// #endregion

// #region scrollbarWidth
export interface ScrollbarWidth {
    (force?: boolean): number | undefined;

    __cache?: number;
}

export const scrollbarWidth: ScrollbarWidth = (force?: boolean): number | undefined => {
    // safety check for SSR
    /* istanbul ignore next */
    if (typeof document === 'undefined') {
        return 0;
    }

    // one more safety check. If document has no body element that means script tag been
    // included before the </body> tag, which in general means that DOM is not ready yet.
    // Furthermore check the document's ready state in case it is presented [IE9+]
    // any interactivity [not 'loading'] will be okay for us
    /* istanbul ignore next */
    if (!document.body || (document.readyState && document.readyState === 'loading')) {
        return undefined;
    }

    // return cached value if we have some
    if (force !== true && typeof scrollbarWidth.__cache === 'number') {
        return scrollbarWidth.__cache;
    }

    const el = document.createElement('div');
    const { style } = el;

    // for the case of weird css rules where div will not be a block element.
    style.display = 'block';
    style.position = 'absolute';
    style.width = '100px';
    style.height = '100px';
    style.left = '-999px';
    style.top = '-999px';
    style.overflow = 'scroll';

    document.body.insertBefore(el, null);

    const { clientWidth } = el;

    // if element still has no width it means DOM is not ready yet
    /* istanbul ignore next */
    if (clientWidth === 0) {
        // remove the element and skip the caching
        document.body.removeChild(el);
        return undefined;
    }

    // clientWidth is the total width of the block - scrollbar width, thus, to get the scrollbar width
    // it works well for desktop, but in mobile browsers scrollbar as absolutely positioned and i
    // dont know ant way to determine mobile browser's scrollbar width, so for mobile that value will be always 0.
    scrollbarWidth.__cache = 100 - clientWidth;

    document.body.removeChild(el);

    return scrollbarWidth.__cache;
};
// #endregion

export const getDOMPermissions = async (
    permissions: ManualPermissionName | ManualPermissionName[],
): Promise<PermissionState | PermissionState[]> => {
    const isSingularPermissionsRequest = typeof permissions === 'string';
    const permissionsToCheck = isSingularPermissionsRequest ? [permissions] : permissions;

    const result = await Promise.all(
        permissionsToCheck.map((permission) => requestBrowserPermission({ name: permission })),
    );

    if (isSingularPermissionsRequest) {
        return result[0].state;
    }

    return result.map(({ state }) => state);
};

// eslint-disable-next-line @typescript-eslint/ban-types
export const lazy = (componentImportFn: Function) =>
    React.lazy(async () => {
        const obj = await componentImportFn();
        return typeof obj.default === 'function' ? obj : obj.default;
    });

export const determineHasScroll = (node: HTMLElement, direction: 'vertical' | 'horizontal') => {
    if (!node) {
        return false;
    }

    const hasOverflow = (property) => ['scroll', 'auto'].includes(property);
    const style = window.getComputedStyle(node);
    if (direction === 'vertical') {
        if (node.clientHeight < node.scrollHeight || hasOverflow(style.overflowY)) {
            return true;
        }
    }

    if (direction === 'horizontal') {
        if (node.clientWidth < node.scrollWidth || hasOverflow(style.overflowX)) {
            return true;
        }
    }

    return false;
};

export const stripHtmlTags = (text: string) => text?.replace(/<\/?[^>]+(>|$)/g, '');

export const generateSelectorClassName = (className: string) => `.${className}`;

export const postpone = (cb: () => void, ms: number | boolean = 0) =>
    setTimeout(cb, (ms as number) || 0);

export const scrollToBottom = (args?: {
    throttle?: boolean;
    timeout?: number;
    node?: HTMLDivElement;
}) => {
    const { throttle = false, timeout = 0, node: _node } = args || {};

    const node: HTMLDivElement | null =
        _node || document.querySelector(generateSelectorClassName(messageListStyles.messageList));

    const scroll = () => {
        if (node) {
            node.scrollTop = node.scrollHeight;
        }
    };

    if (throttle) {
        let i = 0;
        const interval = setInterval(() => {
            scroll();

            if (i === 10) {
                clearInterval(interval);
            }

            i++;
        }, 25);
    } else {
        setTimeout(scroll, timeout);
    }
};

export type TOrientation = 'portrait' | 'landsacpe';

export const lockOrientation = async (orientation: TOrientation = 'portrait') => {
    if (!document.fullscreenElement) {
        try {
            await screenfull.request(document.documentElement, {});
        } catch (error) {
            services.log.error('Request fullscreen failed');
        }
    }

    try {
        await o9n.orientation.lock(orientation);
    } catch (error) {
        services.log.error('Screen rotation lock is not supported');
    }
};

export const unlockOrientation = async () => {
    try {
        await screenfull.exit();
        await await o9n.orientation.unlock();
    } catch (error) {
        services.log.error(error);
    }
};

export const getScrollBarWidth = () => {
    const div = document.createElement('div');
    div.style.overflowY = 'scroll';
    div.style.width = '50px';
    div.style.height = '50px';

    document.body.appendChild(div);

    const innerDiv = document.createElement('div');
    innerDiv.style.width = '100%';
    div.appendChild(innerDiv);

    const scrollbarWidth = div.offsetWidth - innerDiv.offsetWidth;

    document.body.removeChild(div);

    return scrollbarWidth;
};

export const platformAwareCallback = (cb: () => void, platformCondition: boolean) => {
    if (platformCondition) {
        cb();
    }
};

export const grabbScroll = (element: HTMLElement, scrollSpeed = 2) => {
    if (!element) return;

    let isDown = false,
        isDragging = false;
    let startX, startY, scrollLeft, scrollTop;

    const handleElementMouseDown = (event: MouseEvent) => {
        isDown = true;
        isDragging = false;
        startX = event.pageX - element.offsetLeft;
        startY = event.pageY - element.offsetTop;
        scrollLeft = element.scrollLeft;
        scrollTop = element.scrollTop;
    };

    const handleMouseLeave = () => {
        isDown = false;
    };

    const handleMouseUp = () => {
        isDown = false;
        postpone(() => {
            isDragging = false;
        });
    };

    const handleMouseMove = (event: MouseEvent) => {
        if (!isDown) return;
        isDragging = true;
        const x = event.pageX - element.offsetLeft;
        const y = event.pageY - element.offsetTop;
        const walkX = (x - startX) * scrollSpeed;
        const walkY = (y - startY) * scrollSpeed;
        element.scrollLeft = scrollLeft - walkX;
        element.scrollTop = scrollTop - walkY;
    };

    const handleMouseClick = (event: MouseEvent) => {
        if (isDragging) {
            event.preventDefault();
            event.stopPropagation();
        }
    };

    element.addEventListener('click', handleMouseClick);
    element.addEventListener('mousedown', handleElementMouseDown);
    element.addEventListener('mouseleave', handleMouseLeave);
    element.addEventListener('mouseup', handleMouseUp);
    element.addEventListener('mousemove', handleMouseMove);

    return () => {
        element.removeEventListener('click', handleMouseClick);
        element.removeEventListener('mousedown', handleElementMouseDown);
        element.removeEventListener('mouseleave', handleMouseLeave);
        element.removeEventListener('mouseup', handleMouseUp);
        element.removeEventListener('mousemove', handleMouseMove);
    };
};

export const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export const executeAtInterval = async (cb: () => void, max: number, delayInMs = 0) => {
    for (let i = 0; i < max; i++) {
        cb();
        await delay(delayInMs);
    }
};

export const getSelectedText = (cb: (args: { selectedText: string; range: Range }) => void) => {
    const selectObj = window.getSelection();
    const selectedText = selectObj.toString();
    const range = selectObj.getRangeAt(0);

    cb({ selectedText, range });
};

export const getIsReactComponent = (element: Record<string, unknown>) =>
    !!element?.$$typeof || typeof element === 'function';

export const restoreContentEditableCursorPosition = ({
    editableDiv,
    cursorPosition: _cursorPosition,
}: {
    editableDiv: HTMLDivElement;
    cursorPosition?: number;
}) => {
    const createRange = (node, targetPosition) => {
        const range = document.createRange();
        range.selectNode(node);
        range.setStart(node, 0);

        let pos = 0;
        const stack = [node];
        while (stack.length > 0) {
            const current = stack.pop();

            if (current.nodeType === Node.TEXT_NODE) {
                const len = current.textContent.length;
                if (pos + len >= targetPosition) {
                    range.setEnd(current, targetPosition - pos);
                    return range;
                }
                pos += len;
            } else if (current.childNodes && current.childNodes.length > 0) {
                for (let i = current.childNodes.length - 1; i >= 0; i--) {
                    stack.push(current.childNodes[i]);
                }
            }
        }

        // The target position is greater than the
        // length of the contenteditable element.
        range.setEnd(node, node.childNodes.length);
        return range;
    };

    let cursorPosition = _cursorPosition;
    let selection = window.getSelection();
    let range = selection.getRangeAt(0);

    if (!cursorPosition) {
        const clonedRange = range.cloneRange();
        clonedRange.selectNodeContents(editableDiv);
        clonedRange.setEnd(range.endContainer, range.endOffset);

        cursorPosition = clonedRange.toString().length;
    }

    range = createRange(editableDiv, cursorPosition);

    selection = window.getSelection();
    selection.removeAllRanges();
    range.collapse(false);
    selection.removeAllRanges();
    selection.addRange(range);
};

export const pluckCurrentThemeSpecificity = (): ThemeModeType | undefined =>
    document.querySelectorAll('[data-widget-theme]')?.[0]?.getAttribute('data-widget-theme') as
        | ThemeModeType
        | undefined;
